import { useIntl } from '@capsicum/common';
import { Button, Skeleton, SkeletonItem } from '@capsicum/common-web';
import styled, { useTheme } from 'styled-components';

export const AuctionDetailsSkeleton = () => {
	const { colors } = useTheme();
	const { $t } = useIntl();

	return (
		<S.SkeletonContainer data-testid="skeleton-auctionDetails-title-skeleton">
			<S.TitleContainer>
				<Skeleton baseColor={colors.skeleton.default} highlightColor={colors.skeleton.highlight}>
					<SkeletonItem height={27} width={250} />
				</Skeleton>
			</S.TitleContainer>
			<S.MainContainer>
				<S.ImageWrapper>
					<Skeleton baseColor={colors.skeleton.default} highlightColor={colors.skeleton.highlight}>
						<SkeletonItem height={303} />
					</Skeleton>
				</S.ImageWrapper>
				<S.Content>
					<Skeleton baseColor={colors.skeleton.default} highlightColor={colors.skeleton.highlight}>
						<SkeletonItem height={35} />
					</Skeleton>
					<S.AuctionTagsContainer>
						<Skeleton baseColor={colors.skeleton.default} highlightColor={colors.skeleton.highlight}>
							<SkeletonItem borderRadius="9999px" height={28} width={80} />
						</Skeleton>
						<Skeleton baseColor={colors.skeleton.default} highlightColor={colors.skeleton.highlight}>
							<SkeletonItem borderRadius="9999px" height={28} width={80} />
						</Skeleton>
					</S.AuctionTagsContainer>
					<Skeleton baseColor={colors.skeleton.default} highlightColor={colors.skeleton.highlight}>
						<SkeletonItem height={106} />
					</Skeleton>
					<Skeleton baseColor={colors.skeleton.default} highlightColor={colors.skeleton.highlight}>
						<SkeletonItem height={42} />
					</Skeleton>
					<Skeleton baseColor={colors.skeleton.default} highlightColor={colors.skeleton.highlight}>
						<SkeletonItem height={1} />
					</Skeleton>
					<S.BidContainer>
						<Skeleton baseColor={colors.skeleton.default} highlightColor={colors.skeleton.highlight}>
							<SkeletonItem height={24} width={100} />
						</Skeleton>
						<Skeleton baseColor={colors.skeleton.default} highlightColor={colors.skeleton.highlight}>
							<SkeletonItem height={24} width={32} />
						</Skeleton>
					</S.BidContainer>
					<S.DescriptionContainer>
						<Skeleton baseColor={colors.skeleton.default} highlightColor={colors.skeleton.highlight}>
							<SkeletonItem height={42} />
						</Skeleton>
						<Skeleton baseColor={colors.skeleton.default} highlightColor={colors.skeleton.highlight}>
							<SkeletonItem height={42} />
						</Skeleton>
						<Skeleton baseColor={colors.skeleton.default} highlightColor={colors.skeleton.highlight}>
							<SkeletonItem height={42} />
						</Skeleton>
					</S.DescriptionContainer>

					<S.ButtonWrapper>
						<Button
							color="brand"
							disabled
							testID="skeleton-auctionDetails-button-redeem"
							text={$t('rewards.auctions.placeABid')}
						/>
					</S.ButtonWrapper>
				</S.Content>
			</S.MainContainer>
		</S.SkeletonContainer>
	);
};

namespace S {
	export const SkeletonContainer = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.large};
	`;

	export const TitleContainer = styled.div`
		width: 100%;
	`;

	export const MainContainer = styled.div`
		display: flex;
		gap: ${({ theme }) => theme.spacings.medium};
		padding: ${({ theme }) => theme.spacings.small};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		${({ theme }) => theme.border.radius.medium};

		${({ theme }) => theme.breakpoints.queries.below.mobile} {
			flex-direction: column;
		}
	`;

	export const ImageWrapper = styled.div`
		display: flex;
		flex: 1 0 0;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.medium};
	`;

	export const Content = styled.div`
		display: flex;
		flex: 1 0 0;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.small};
	`;

	export const AuctionTagsContainer = styled.div`
		display: flex;
		flex-direction: row;
		align-self: stretch;
		flex-wrap: wrap;
		gap: ${({ theme }) => theme.spacings.xxsmall};
		${({ theme }) => theme.border.radius.circle};
	`;

	export const BidContainer = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: space-between;
	`;

	export const DescriptionContainer = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const ButtonWrapper = styled.div`
		display: flex;
		align-self: flex-end;
		align-items: center;

		${({ theme }) => theme.breakpoints.queries.below.mobile} {
			position: fixed;
			display: grid;
			bottom: 0;
			left: 0;
			right: 0;
			z-index: 10;
			padding: ${({ theme }) => `${theme.spacings.xsmall} ${theme.spacings.small}`};
			background: ${({ theme }) => theme.colors.backgrounds.main.translucent};
			border-top: ${({ theme }) => `1px solid ${theme.colors.borders.main.divider}`};
			backdrop-filter: blur(2px);
		}
	`;
}
